import { createSlice } from '@reduxjs/toolkit';

// Initial state that exactly mirrors ALL Recoil atoms
const initialState = {
  // Basic app state
  menu: [], // mirrors appMenu atom
  user: {}, // mirrors appUser atom
  filterDelay: "all", // mirrors appFilterDelay atom
  categoryTabs: [], // mirrors appCategoryTabs atom
  
  // Document and PO management
  getDocument: [], // mirrors appGetDocument atom
  updatePO: false, // mirrors appUpdatePO atom
  getPoList: [], // mirrors appGetPoList atom
  getAlertInfo: {}, // mirrors appGetAlertInfo atom
  showBadge: {}, // mirrors appShowBadge atom
  poLine: "", // mirrors appPoLine atom
  editPo: false, // mirrors appEditPo atom
  addSupplier: false, // mirrors appAddSupplier atom
  poId: {}, // mirrors appPoId atom
  
  // Authentication and system state
  login: false, // mirrors appLogin atom
  changeIncoterm: null, // mirrors appChangeIncoterm atom
  requestUpdate: null, // mirrors appRequestUpdate atom
  documentUpload: null, // mirrors appDocumentUpload atom
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // Basic app actions
    setMenu: (state, action) => {
      state.menu = action.payload;
    },
    clearMenu: (state) => {
      state.menu = [];
    },
    
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = {};
    },
    
    setFilterDelay: (state, action) => {
      state.filterDelay = action.payload;
    },
    
    setCategoryTabs: (state, action) => {
      state.categoryTabs = action.payload;
    },
    clearCategoryTabs: (state) => {
      state.categoryTabs = [];
    },
    
    // Document and PO management actions
    setGetDocument: (state, action) => {
      state.getDocument = action.payload;
    },
    clearGetDocument: (state) => {
      state.getDocument = [];
    },
    
    setUpdatePO: (state, action) => {
      state.updatePO = action.payload;
    },
    
    setGetPoList: (state, action) => {
      state.getPoList = action.payload;
    },
    clearGetPoList: (state) => {
      state.getPoList = [];
    },
    
    setGetAlertInfo: (state, action) => {
      state.getAlertInfo = action.payload;
    },
    clearGetAlertInfo: (state) => {
      state.getAlertInfo = {};
    },
    
    setShowBadge: (state, action) => {
      state.showBadge = action.payload;
    },
    clearShowBadge: (state) => {
      state.showBadge = {};
    },
    
    setPoLine: (state, action) => {
      state.poLine = action.payload;
    },
    clearPoLine: (state) => {
      state.poLine = "";
    },
    
    setEditPo: (state, action) => {
      state.editPo = action.payload;
    },
    
    setAddSupplier: (state, action) => {
      state.addSupplier = action.payload;
    },
    
    setPoId: (state, action) => {
      state.poId = action.payload;
    },
    clearPoId: (state) => {
      state.poId = {};
    },
    
    // Authentication and system actions
    setLogin: (state, action) => {
      state.login = action.payload;
    },
    
    setChangeIncoterm: (state, action) => {
      state.changeIncoterm = action.payload;
    },
    clearChangeIncoterm: (state) => {
      state.changeIncoterm = null;
    },
    
    setRequestUpdate: (state, action) => {
      state.requestUpdate = action.payload;
    },
    clearRequestUpdate: (state) => {
      state.requestUpdate = null;
    },
    
    setDocumentUpload: (state, action) => {
      state.documentUpload = action.payload;
    },
    clearDocumentUpload: (state) => {
      state.documentUpload = null;
    },

    // Bulk action to mimic appStateController functionality
    dispatchAppAction: (state, action) => {
      const { actionType, payload } = action.payload;
      
      switch (actionType) {
        case 'UPDATE_MENU':
          state.menu = payload;
          break;
        case 'GET_USER':
          state.user = payload;
          break;
        case 'CATEGORY_TABS':
          state.categoryTabs = payload;
          break;
        case 'FILTER_DELAY':
          state.filterDelay = payload;
          break;
        case 'GET_DOCUMENT':
          state.getDocument = payload;
          break;
        case 'UPDATE_PO':
          state.updatePO = payload;
          break;
        case 'GET_PO_LIST':
          state.getPoList = payload;
          break;
        case 'GET_ALERT_INFO':
          state.getAlertInfo = payload;
          break;
        case 'SHOW_BADGE':
          state.showBadge = payload;
          break;
        case 'PO_LINE':
          state.poLine = payload;
          break;
        case 'EDIT_PO':
          state.editPo = payload;
          break;
        case 'ADD_SUPPLIER':
          state.addSupplier = payload;
          break;
        case 'PO_ID':
          state.poId = payload;
          break;
        case 'LOGIN':
          state.login = payload;
          break;
        case 'CHANGE_INCOTERM':
          state.changeIncoterm = payload;
          break;
        case 'REQUEST_UPDATE':
          state.requestUpdate = payload;
          break;
        case 'DOCUMENT_UPLOAD':
          state.documentUpload = payload;
          break;
        default:
          break;
      }
    },
  },
});

// Export individual actions
export const {
  // Basic app actions
  setMenu,
  clearMenu,
  setUser,
  clearUser,
  setFilterDelay,
  setCategoryTabs,
  clearCategoryTabs,
  
  // Document and PO actions
  setGetDocument,
  clearGetDocument,
  setUpdatePO,
  setGetPoList,
  clearGetPoList,
  setGetAlertInfo,
  clearGetAlertInfo,
  setShowBadge,
  clearShowBadge,
  setPoLine,
  clearPoLine,
  setEditPo,
  setAddSupplier,
  setPoId,
  clearPoId,
  
  // Authentication and system actions
  setLogin,
  setChangeIncoterm,
  clearChangeIncoterm,
  setRequestUpdate,
  clearRequestUpdate,
  setDocumentUpload,
  clearDocumentUpload,
  
  // Bulk action dispatcher
  dispatchAppAction,
} = appSlice.actions;

// Export selectors (to match Recoil atom patterns)
export const selectMenu = (state) => state.app.menu;
export const selectUser = (state) => state.app.user;
export const selectFilterDelay = (state) => state.app.filterDelay;
export const selectCategoryTabs = (state) => state.app.categoryTabs;
export const selectGetDocument = (state) => state.app.getDocument;
export const selectUpdatePO = (state) => state.app.updatePO;
export const selectGetPoList = (state) => state.app.getPoList;
export const selectGetAlertInfo = (state) => state.app.getAlertInfo;
export const selectShowBadge = (state) => state.app.showBadge;
export const selectPoLine = (state) => state.app.poLine;
export const selectEditPo = (state) => state.app.editPo;
export const selectAddSupplier = (state) => state.app.addSupplier;
export const selectPoId = (state) => state.app.poId;
export const selectLogin = (state) => state.app.login;
export const selectChangeIncoterm = (state) => state.app.changeIncoterm;
export const selectRequestUpdate = (state) => state.app.requestUpdate;
export const selectDocumentUpload = (state) => state.app.documentUpload;

export default appSlice.reducer; 